import React, { useMemo, useState } from 'react'
import { useLocale, Card, Button, MealCard, Spinner } from '@betina/ui'
import { useQuery, useMutation } from '../hooks/useApi.js'
import { mealPlanApi, dishLogApi, dishesApi, aiApi } from '../lib/api.js'
import { WeeklyReview, useWeeklyReviewPrompt } from '../components/WeeklyReview.jsx'
import { MarmitonSuggestions } from '../components/MarmitonSuggestions.jsx'
import { NextMealHero } from '../components/NextMealHero.jsx'
import { DishLibraryPicker } from '../components/DishLibraryPicker.jsx'
import { ShoppingListDrawer } from '../components/ShoppingListDrawer.jsx'
import { ConstraintsChips } from '../components/ConstraintsChips.jsx'
import { DaySummary } from '../components/DaySummary.jsx'
import { PlannerDnd, DroppableSlot, DraggableMeal } from '../components/PlannerDnd.jsx'

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']
const DAYS_FR_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
const DAYS_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner']

// Monday-indexed day-of-week: Mon=0..Sun=6.
function getMondayIdx() {
  const d = new Date().getDay()
  return d === 0 ? 6 : d - 1
}

function getMonday(offset = 0) {
  const d = new Date()
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) + offset * 7
  d.setDate(diff)
  return d.toISOString().slice(0, 10)
}

export function Planner() {
  const { t, locale } = useLocale()
  const [weekOffset, setWeekOffset] = useState(0)
  const [view, setView] = useState(() => {
    // Default to "today" view mid-week, "week" on weekends/Mondays.
    const dow = getMondayIdx()
    return dow >= 1 && dow <= 4 ? 'today' : 'week'
  })
  const [addingSlot, setAddingSlot] = useState(null) // { day, mealType }
  const [aiLoading, setAiLoading] = useState(false)
  const [aiPlan, setAiPlan] = useState(null)
  const [aiConstraints, setAiConstraints] = useState([])
  const [showMarmiton, setShowMarmiton] = useState(false)
  const [showReview, setShowReview] = useState(false)
  const [showShopping, setShowShopping] = useState(false)

  const { shouldShow: reviewPrompt, dismiss: dismissReviewPrompt } = useWeeklyReviewPrompt()

  const weekStart = getMonday(weekOffset)
  const days = locale === 'fr' ? DAYS_FR : DAYS_EN
  const isCurrentWeek = weekOffset === 0
  const todayIdx = getMondayIdx()

  const { data: plan, loading, refetch } = useQuery(
    () => mealPlanApi.get(weekStart),
    [weekStart],
  )

  const { mutate: createPlan } = useMutation(() => mealPlanApi.create(weekStart))
  const { mutate: updateStatus } = useMutation(({ slotId, status }) =>
    mealPlanApi.updateSlotStatus(slotId, status),
  )
  const { mutate: logDish } = useMutation((data) => dishLogApi.create(data))

  async function ensurePlan() {
    if (plan?.id) return plan.id
    const created = await createPlan()
    await refetch()
    return created.id
  }

  async function handleAddMeal(mealName, options = {}) {
    if (!mealName?.trim()) return
    const planId = await ensurePlan()
    await mealPlanApi.addSlot(planId, {
      dayOfWeek: addingSlot.day,
      mealType: addingSlot.mealType,
      customMeal: mealName,
      ...(options.prepTime ? { prepTime: options.prepTime } : {}),
    })
    setAddingSlot(null)
    refetch()
  }

  async function handlePrepared(slot) {
    await logDish({ name: slot.custom_meal || 'Repas', mealSlotId: slot.id })
    await updateStatus({ slotId: slot.id, status: 'prepared' })
    refetch()
  }

  async function handleSkip(slot) {
    await updateStatus({ slotId: slot.id, status: 'skipped' })
    refetch()
  }

  async function handleDeleteSlot(slot) {
    await mealPlanApi.deleteSlot(slot.id)
    refetch()
  }

  async function handleSaveToLibrary(slot) {
    if (!slot.custom_meal) return
    await dishesApi.save({ name: slot.custom_meal, prepTime: slot.prep_time })
  }

  async function handleAISuggest() {
    setAiLoading(true)
    try {
      const res = await aiApi.suggestMeals({ weekStart, constraints: aiConstraints })
      setAiPlan(res?.plan)
    } finally {
      setAiLoading(false)
    }
  }

  async function applyAIPlan() {
    if (!aiPlan?.days) return
    const planId = await ensurePlan()
    for (const day of aiPlan.days) {
      for (const [mealType, meal] of Object.entries(day.meals ?? {})) {
        if (meal?.name) {
          await mealPlanApi.addSlot(planId, {
            dayOfWeek: day.dayOfWeek,
            mealType,
            customMeal: meal.name,
          })
        }
      }
    }
    setAiPlan(null)
    refetch()
  }

  async function handleMarmitonAdd(mealName) {
    const planId = await ensurePlan()
    const slots = plan?.slots ?? []
    const occupiedDays = new Set(
      slots.filter((s) => s.meal_type === 'dinner').map((s) => s.day_of_week),
    )
    const freeDayIdx = [0, 1, 2, 3, 4, 5, 6].find((d) => !occupiedDays.has(d))
    const dayOfWeek = freeDayIdx ?? 0
    await mealPlanApi.addSlot(planId, {
      dayOfWeek,
      mealType: 'dinner',
      customMeal: mealName,
    })
    refetch()
    setShowMarmiton(false)
  }

  async function handleDrop({ from, to }) {
    if (!plan?.id) return
    await mealPlanApi.moveSlot(plan.id, {
      fromDay: from.dayOfWeek,
      fromMeal: from.mealType,
      toDay: to.dayOfWeek,
      toMeal: to.mealType,
    })
    refetch()
  }

  function handleHeroSuggest() {
    handleAISuggest()
  }

  const slotsByDay = useMemo(() => {
    const map = {}
    for (const s of plan?.slots ?? []) {
      if (!map[s.day_of_week]) map[s.day_of_week] = []
      map[s.day_of_week].push(s)
    }
    return map
  }, [plan])

  return (
    <div className="p-4 space-y-4">
      {/* ── End-of-week review banner ─────────────────────────────────────── */}
      {reviewPrompt && !showReview && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-amber-800">🍽️ Bilan de semaine</p>
            <p className="text-xs text-amber-600 mt-0.5">Coche les plats préparés et note-les !</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => setShowReview(true)}
              className="px-3 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-lg hover:bg-amber-600 transition-colors"
            >
              Faire le bilan
            </button>
            <button
              onClick={dismissReviewPrompt}
              className="px-2 py-1.5 text-amber-500 text-xs hover:text-amber-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── P1: Hero "Prochain repas" ─────────────────────────────────────── */}
      {!loading && (
        <NextMealHero
          slots={plan?.slots ?? []}
          todayDayOfWeek={todayIdx}
          isCurrentWeek={isCurrentWeek}
          onPrepared={handlePrepared}
          onSkip={handleSkip}
          onSuggest={handleHeroSuggest}
          onAdd={(addSpec) => setAddingSlot(addSpec)}
        />
      )}

      {/* ── Week navigation ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <button onClick={() => setWeekOffset((o) => o - 1)} className="p-2 text-gray-500 hover:text-gray-700">
          ←
        </button>
        <div className="text-center">
          <p className="font-semibold text-gray-800">
            {t('week')} {weekOffset === 0 ? '(actuelle)' : weekOffset > 0 ? `+${weekOffset}` : weekOffset}
          </p>
          <p className="text-xs text-gray-400">{weekStart}</p>
        </div>
        <button onClick={() => setWeekOffset((o) => o + 1)} className="p-2 text-gray-500 hover:text-gray-700">
          →
        </button>
      </div>

      {/* ── P4: View toggle (Today / Week) ────────────────────────────────── */}
      {isCurrentWeek && (
        <div className="flex bg-gray-100 rounded-xl p-1 text-sm">
          <ToggleButton active={view === 'today'} onClick={() => setView('today')}>
            Aujourd'hui
          </ToggleButton>
          <ToggleButton active={view === 'week'} onClick={() => setView('week')}>
            Semaine
          </ToggleButton>
        </div>
      )}

      {/* ── Action buttons ────────────────────────────────────────────────── */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant="secondary"
          className="flex-1 justify-center min-w-0"
          loading={aiLoading}
          onClick={handleAISuggest}
          icon={<span>✨</span>}
        >
          {t('ai_suggest')}
        </Button>

        <button
          onClick={() => setShowMarmiton(true)}
          className="flex-1 min-w-0 flex items-center justify-center gap-1.5 rounded-xl border border-orange-200 bg-orange-50 text-orange-700 text-sm font-medium px-3 py-2 hover:bg-orange-100 transition-colors"
        >
          <span>🍴</span> Marmiton
        </button>

        <button
          onClick={() => setShowShopping(true)}
          className="flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 px-3 py-2 hover:bg-gray-50 transition-colors text-base"
          title="Liste de courses"
        >
          🛒
        </button>

        <button
          onClick={() => setShowReview(true)}
          title="Bilan de semaine"
          className="flex items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-500 px-3 py-2 hover:bg-gray-100 transition-colors text-base"
        >
          📋
        </button>
      </div>

      {/* ── P5: AI constraints chips ──────────────────────────────────────── */}
      <ConstraintsChips onChange={setAiConstraints} />

      {/* ── AI plan preview ───────────────────────────────────────────────── */}
      {aiPlan && (
        <Card className="border-emerald-200 bg-emerald-50">
          <p className="text-sm font-semibold text-emerald-700 mb-2">✨ Plan IA pour la semaine</p>
          {aiPlan.days?.slice(0, 3).map((day) => (
            <div key={day.dayOfWeek} className="text-xs text-gray-600 mb-1">
              <span className="font-medium">{day.dayName}:</span>{' '}
              {Object.values(day.meals ?? {}).map((m) => m?.name).filter(Boolean).join(' · ')}
            </div>
          ))}
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={applyAIPlan}>Appliquer ce plan</Button>
            <Button size="sm" variant="ghost" onClick={() => setAiPlan(null)}>Ignorer</Button>
          </div>
        </Card>
      )}

      {/* ── Grid (Today or Week) ──────────────────────────────────────────── */}
      {loading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : view === 'today' && isCurrentWeek ? (
        <TodayView
          dayName={days[todayIdx]}
          daySlots={slotsByDay[todayIdx] ?? []}
          mealTypes={MEAL_TYPES}
          onAdd={(type) => setAddingSlot({ day: todayIdx, mealType: type })}
          onPrepared={handlePrepared}
          onSkip={handleSkip}
          onDelete={handleDeleteSlot}
          onSaveToLibrary={handleSaveToLibrary}
          t={t}
        />
      ) : (
        <PlannerDnd onDrop={handleDrop}>
          <div className="space-y-4">
            {days.map((dayName, idx) => {
              const daySlots = slotsByDay[idx] ?? []
              const isToday = isCurrentWeek && idx === todayIdx
              return (
                <div
                  key={idx}
                  className={`rounded-xl p-3 -mx-1 ${
                    isToday ? 'bg-emerald-50/50 ring-1 ring-emerald-200' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`text-sm font-semibold ${isToday ? 'text-emerald-700' : 'text-gray-700'}`}>
                      {dayName}
                      {isToday && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-emerald-600 text-white px-1.5 py-0.5 rounded">Aujourd'hui</span>}
                    </h3>
                  </div>
                  <div className="space-y-1.5">
                    {MEAL_TYPES.map((type) => {
                      const slot = daySlots.find((s) => s.meal_type === type)
                      return (
                        <DroppableSlot key={type} dayOfWeek={idx} mealType={type}>
                          {slot ? (
                            <DraggableMeal slot={slot} label={slot.custom_meal}>
                              <MealCard
                                meal={{ ...slot, name: slot.custom_meal }}
                                onPrepared={handlePrepared}
                                onSkip={handleSkip}
                                onDelete={handleDeleteSlot}
                                onSaveToLibrary={handleSaveToLibrary}
                              />
                            </DraggableMeal>
                          ) : (
                            <button
                              onClick={() => setAddingSlot({ day: idx, mealType: type })}
                              className="w-full text-left text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-2 hover:border-emerald-300 hover:text-emerald-500 transition-colors"
                            >
                              + {t(type)}
                            </button>
                          )}
                        </DroppableSlot>
                      )
                    })}
                  </div>
                  {/* P7: Day summary footer */}
                  <DaySummary slots={daySlots} mealTypes={MEAL_TYPES} />
                </div>
              )
            })}
          </div>
        </PlannerDnd>
      )}

      {/* ── P2: Add meal modal (Library + Free text) ──────────────────────── */}
      {addingSlot && (
        <DishLibraryPicker
          slotLabel={`${days[addingSlot.day]} — ${t(addingSlot.mealType)}`}
          onSelect={handleAddMeal}
          onClose={() => setAddingSlot(null)}
        />
      )}

      {/* ── Marmiton suggestions panel ────────────────────────────────────── */}
      {showMarmiton && (
        <MarmitonSuggestions
          onAddMeal={handleMarmitonAdd}
          onClose={() => setShowMarmiton(false)}
        />
      )}

      {/* ── P3: Shopping list drawer ──────────────────────────────────────── */}
      {showShopping && (
        <ShoppingListDrawer weekStart={weekStart} onClose={() => setShowShopping(false)} />
      )}

      {/* ── Weekly review modal ───────────────────────────────────────────── */}
      {showReview && (
        <WeeklyReview
          onClose={() => {
            setShowReview(false)
            dismissReviewPrompt()
          }}
        />
      )}
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function ToggleButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-3 py-1.5 rounded-lg font-medium transition-colors ${
        active ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  )
}

function TodayView({ dayName, daySlots, mealTypes, onAdd, onPrepared, onSkip, onDelete, onSaveToLibrary, t }) {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-gray-800">{dayName}</h3>
      {mealTypes.map((type) => {
        const slot = daySlots.find((s) => s.meal_type === type)
        return (
          <div key={type} className="rounded-2xl border border-gray-100 bg-white shadow-sm p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t(type)}
            </p>
            {slot ? (
              <div>
                <p className="font-semibold text-gray-800 text-base">{slot.custom_meal}</p>
                {slot.prep_time && (
                  <p className="text-xs text-gray-400 mt-0.5">~{slot.prep_time} min</p>
                )}
                <div className="flex gap-2 mt-3">
                  {slot.status === 'planned' && (
                    <>
                      <button
                        onClick={() => onPrepared(slot)}
                        className="flex-1 px-3 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700"
                      >
                        ✓ Préparé
                      </button>
                      <button
                        onClick={() => onSkip(slot)}
                        className="px-3 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200"
                      >
                        Passer
                      </button>
                    </>
                  )}
                  {slot.status === 'prepared' && (
                    <span className="text-sm text-emerald-600 font-semibold">✓ Préparé</span>
                  )}
                  {slot.status === 'skipped' && (
                    <span className="text-sm text-red-500">Passé</span>
                  )}
                  <button
                    onClick={() => onSaveToLibrary(slot)}
                    className="px-3 py-2 text-gray-400 hover:text-amber-500"
                    title="Sauver dans mes plats"
                  >
                    💾
                  </button>
                  <button
                    onClick={() => onDelete(slot)}
                    className="px-3 py-2 text-gray-300 hover:text-red-500"
                    title="Supprimer"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => onAdd(type)}
                className="w-full text-left text-sm text-gray-400 border-2 border-dashed border-gray-200 rounded-xl px-4 py-6 hover:border-emerald-300 hover:text-emerald-500 transition-colors"
              >
                + Ajouter un plat
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

// re-export day labels in case anyone needs them
export { DAYS_FR_SHORT }
